import React from 'react';
import { ArrowRightIcon, CalendarIcon, TrendingUpIcon, UsersIcon, DollarSignIcon } from 'lucide-react';
import { AnimatedMetric } from './AnimatedMetric';
interface Metric {
  label: string;
  value: number;
  prefix?: string;
  suffix?: string;
  icon: 'trending' | 'users' | 'dollar';
}
interface DetailedCaseStudyCardProps {
  industry: string;
  companyProfile: string;
  challenge: string;
  solution: string;
  results: Metric[];
  status: string;
  delay?: number;
  pdfUrl?: string;
  logoUrl?: string;
  note?: string;
  onReadMore?: () => void;
}
export function DetailedCaseStudyCard({
  industry,
  companyProfile,
  challenge,
  solution,
  results,
  status,
  delay = 0,
  pdfUrl = '#',
  logoUrl,
  note,
  onReadMore
}: DetailedCaseStudyCardProps) {
  const iconMap = {
    trending: TrendingUpIcon,
    users: UsersIcon,
    dollar: DollarSignIcon
  };
  const industryColors: Record<string, string> = {
    Solar: 'bg-yellow-500',
    Roofing: 'bg-blue-500',
    'Pest Control': 'bg-green-500',
    Windows: 'bg-purple-500',
    Telecom: 'bg-indigo-500',
    HVAC: 'bg-red-500',
    'Life Insurance': 'bg-emerald-500',
    Energy: 'bg-orange-500',
    Remodeling: 'bg-pink-500'
  };
  return <div className="bg-white rounded shadow-lg border border-gray-200 overflow-hidden hover:shadow-2xl hover:border-mint transition-all group flex flex-col h-full" style={{
    animation: `fadeInUp 0.6s ease-out ${delay}s both`
  }} data-id="element-1159">
      {/* Header */}
      <div className="p-6 border-b border-gray-100" data-id="element-1160">
        <div className="flex items-center justify-between mb-4" data-id="element-1161">
          <span className={`inline-block ${industryColors[industry] || 'bg-mint'} text-white px-3 py-1 rounded text-sm font-bold`} data-id="element-1162">
            {industry}
          </span>
          <div className="flex items-center text-sm text-gray-500" data-id="element-1163">
            <CalendarIcon className="h-4 w-4 mr-1" data-id="element-1164" />
            {status}
          </div>
        </div>
        <p className="text-gray-600 font-medium" data-id="element-1165">{companyProfile}</p>
      </div>

      {/* Logo Section - Navy background for contrast */}
      {logoUrl && <div className="bg-deep-blue p-6 flex items-center justify-center" data-id="element-1166">
          <img src={logoUrl} alt={`${industry} company logo`} className="h-16 w-auto object-contain" data-id="element-1167" />
        </div>}

      {/* Content */}
      <div className="p-6 flex-1 flex flex-col" data-id="element-1168">
        {/* Challenge */}
        <div className="mb-6" data-id="element-1169">
          <h4 className="text-sm font-bold text-gray-700 mb-2 flex items-center" data-id="element-1170">
            <span className="w-6 h-6 bg-gray-200 rounded flex items-center justify-center mr-2 text-xs" data-id="element-1171">
              !
            </span>
            The Challenge
          </h4>
          <p className="text-gray-600 text-sm leading-relaxed" data-id="element-1172">{challenge}</p>
        </div>

        {/* Solution */}
        <div className="mb-6" data-id="element-1173">
          <h4 className="text-sm font-bold text-gray-700 mb-2 flex items-center" data-id="element-1174">
            <span className="w-6 h-6 bg-mint rounded flex items-center justify-center mr-2 text-xs text-deep-blue" data-id="element-1175">
              ✓
            </span>
            The Solution
          </h4>
          <p className="text-gray-600 text-sm leading-relaxed" data-id="element-1176">{solution}</p>
          {note && <p className="text-xs text-gray-500 italic mt-2" data-id="element-1177">{note}</p>}
        </div>

        {/* Results - flex-1 pushes this to take remaining space */}
        <div className="mb-6 bg-gray-50 rounded p-4 flex-1 flex flex-col justify-center" data-id="element-1178">
          <h4 className="text-sm font-bold text-gray-700 mb-4" data-id="element-1179">The Results</h4>
          <div className="grid grid-cols-3 gap-4" data-id="element-1180">
            {results.map((result, index) => {
            const Icon = iconMap[result.icon];
            return <div key={index} className="text-center" data-id="element-1181">
                  <Icon className="h-5 w-5 text-mint mx-auto mb-2" data-id="element-1182" />
                  <div className="text-2xl font-bold text-deep-blue" data-id="element-1183">
                    <AnimatedMetric value={result.value} prefix={result.prefix} suffix={result.suffix} duration={1500} data-id="element-1184" />
                  </div>
                  <div className="text-xs text-gray-600 mt-1" data-id="element-1185">
                    {result.label}
                  </div>
                </div>;
          })}
          </div>
        </div>

        {/* CTA */}
        <button onClick={onReadMore} className="w-full flex items-center justify-center text-mint font-semibold hover:text-mint-dark transition-colors group-hover:translate-x-1 transition-transform" data-id="element-1186">
          Read Full Story <ArrowRightIcon className="ml-2 h-4 w-4" data-id="element-1187" />
        </button>
      </div>
    </div>;
}